import { useEffect, useState } from "react";
import { Button, OverlayTrigger, Popover } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import MenuConfig from "../../../constants/MenuConfig";
import ModalPage from "../../components/Common/Modal/ModalPage";
import { formatDate, formatDateTime } from "../../../utils/DateUtils";
import "./SubscriptionRefundAlert.css";

/*
 * Mensalidades devolvidas e a diferença de Assinaturas entre as Comissões e o painel.
 *
 * O painel segue o caixa e desconta toda devolução no mês em que ela acontece. As Comissões usam a
 * base do pote, que não desconta a devolução de mensalidade paga antes de 01/10/2026: ela já ficou
 * fora do pote do mês em que foi paga, e descontar de novo tiraria o valor da equipe duas vezes.
 * O alerta fica no total de Assinaturas de cada filial, que é o número que o gestor compara com o
 * painel, e mostra a conta com os números daquela linha.
 */

/** Arredonda em centavos, como os valores aparecem na tela. */
const round2 = (value) => Math.round(value * 100) / 100;

/** Percentual com duas casas, no formato da tela. */
const formatPercent = (value) =>
    `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/**
 * Devoluções de mensalidade do período e da filial da lista. Uma consulta só por tela: o alerta
 * aparece em várias linhas de total e todas leem a mesma lista.
 *
 * @param period período da lista ({ startDate, endDate } em AAAA-MM-DD).
 * @param branchId filial do cabeçalho; nula em "Todas as filiais".
 * @param disabled período inválido: não consulta.
 */
export const useSubscriptionRefunds = (period, branchId, disabled) => {
    const [refunds, setRefunds] = useState([]);

    useEffect(() => {
        if (disabled) {
            setRefunds([]);
            return;
        }
        let active = true;
        axiosInstance.get(`${Api.reports.employee_statement}/subscription-refunds`, { params: { ...period, branchId } })
            .then((resp) => active && setRefunds(resp.data || []))
            // A lista só explica a diferença com o painel; se falhar, a tela segue sem o alerta.
            .catch(() => active && setRefunds([]));
        return () => { active = false; };
    }, [period.startDate, period.endDate, branchId, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return refunds;
};

/**
 * Quanto das devoluções não descontadas pesa na base da lista. Com filial no cabeçalho a base já é
 * a parte dela, então pesa a parte dela; na empresa inteira, o valor cheio.
 */
const summarize = (refunds) => {
    const notDeducted = refunds.filter((refund) => !refund.deducted);
    const value = notDeducted.reduce((total, refund) => total + Number(refund.value || 0), 0);
    const baseShare = notDeducted.reduce(
        (total, refund) => total + Number(refund.scopeValue ?? refund.value ?? 0), 0);
    return { notDeducted, value, baseShare };
};

/**
 * Alerta na célula de Assinaturas de uma linha de total. Só aparece quando há devolução não
 * descontada, que é quando esse total fica acima do painel.
 *
 * @param refunds lista de useSubscriptionRefunds.
 * @param groupTotal total de Assinaturas exibido na linha.
 * @param baseValue "Valor recebido de assinaturas" oficial da lista (a base inteira).
 * @param groupLabel nome da filial da linha; nulo na linha da empresa inteira.
 * @param hidden base digitada para simular: os números da linha não são os oficiais.
 * @param formatCurrency o mesmo formatador de moeda da tela.
 * @param onViewDetails abre a janela com as devoluções.
 */
export const SubscriptionRefundAlert = ({ refunds, groupTotal, baseValue, groupLabel, hidden,
    formatCurrency, onViewDetails }) => {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const { notDeducted, value, baseShare } = summarize(refunds);
    const base = Number(baseValue || 0);

    if (hidden || notDeducted.length === 0 || base <= 0) {
        return null;
    }

    // A linha leva a fração da base que ela representa. Os dois lados são arredondados como as telas
    // mostram, para a diferença ser a mesma que o gestor calcula olhando o painel e esta linha.
    const total = Number(groupTotal || 0);
    const groupShare = total * baseShare / base;
    const shown = round2(total);
    const dashboardValue = round2(total - groupShare);
    const difference = round2(shown - dashboardValue);

    const popover = (
        <Popover className="hint-popover subscription-refund-alert__popover">
            <Popover.Body>
                <p className="mb-2">
                    {t('employee_statement_refunds_not_deducted', {
                        count: notDeducted.length,
                        total: refunds.length,
                        value: formatCurrency(value)
                    })}
                </p>
                {/* Sem isto, quem olha uma filial procura o valor cheio da devolução na diferença e
                    não acha: a filial só fica com a parte dela. */}
                {groupLabel && (
                    <p className="mb-2">
                        {t('employee_statement_refunds_branch_part', {
                            branch: groupLabel,
                            share: formatCurrency(difference),
                            percent: formatPercent(value > 0 ? groupShare * 100 / value : 0)
                        })}
                    </p>
                )}
                <p className="mb-2 subscription-refund-alert__compare">
                    {t('employee_statement_refunds_dashboard_compare', {
                        dashboard: formatCurrency(dashboardValue),
                        here: formatCurrency(shown),
                        diff: formatCurrency(difference)
                    })}
                </p>
                <Button size="sm" variant="primary" onClick={() => {
                    // Fecha a caixa antes de abrir a janela, senão ela fica por cima do modal.
                    setShow(false);
                    onViewDetails();
                }}>
                    {t('employee_statement_refunds_view')}
                </Button>
            </Popover.Body>
        </Popover>
    );

    return (
        <OverlayTrigger trigger="click" placement="auto" rootClose overlay={popover} show={show} onToggle={setShow}>
            <button type="button" className="subscription-refund-alert__trigger"
                aria-label={t('employee_statement_refunds_title')}>
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            </button>
        </OverlayTrigger>
    );
};

/**
 * Janela com as devoluções do período. Uma por tela, aberta pelo alerta de qualquer linha.
 *
 * @param baseValue "Valor recebido de assinaturas" oficial, para a parte da filial no rodapé bater
 *                  com a diferença mostrada no alerta.
 */
export const SubscriptionRefundDetails = ({ refunds, show, setShow, baseValue, formatCurrency }) => {
    const { t } = useTranslation();
    const { notDeducted, value, baseShare } = summarize(refunds);
    const hasBranchShare = notDeducted.some((refund) => refund.scopeValue != null);
    // Com filial no cabeçalho a base inteira é da filial: a parte dela é a diferença da linha de total.
    const base = Number(baseValue || 0);
    const branchDifference = round2(round2(base) - round2(base - baseShare));

    return (
        <ModalPage
            title={t('employee_statement_refunds_title')}
            show={show}
            setShow={setShow}
            size="lg"
            dialogClassName="subscription-refund-alert__dialog"
            child={(
                <div className="subscription-refund-alert__list">
                    {refunds.map((refund) => (
                        <div key={refund.transactionId} className="subscription-refund-alert__item">
                            <div className="subscription-refund-alert__item-head">
                                <strong>{refund.customerName || t('employee_statement_refunds_no_customer')}</strong>
                                <span className={`subscription-refund-alert__status${refund.deducted ? '' : ' is-warning'}`}>
                                    {refund.deducted
                                        ? t('employee_statement_refunds_deducted')
                                        : t('employee_statement_refunds_not_deducted_status')}
                                </span>
                            </div>
                            <dl className="subscription-refund-alert__facts">
                                <div>
                                    <dt>{t('employee_statement_refunds_value')}</dt>
                                    <dd>{formatCurrency(refund.value)}</dd>
                                </div>
                                {refund.scopeValue != null && (
                                    <div>
                                        <dt>{t('employee_statement_refunds_branch_share')}</dt>
                                        <dd>{formatCurrency(refund.scopeValue)}</dd>
                                    </div>
                                )}
                                <div>
                                    <dt>{t('employee_statement_refunds_paid_at')}</dt>
                                    <dd>{refund.paidAt ? formatDate(refund.paidAt) : '-'}</dd>
                                </div>
                                <div>
                                    <dt>{t('employee_statement_refunds_refunded_at')}</dt>
                                    <dd>{formatDateTime(refund.refundedAt)}</dd>
                                </div>
                            </dl>
                            {!refund.deducted && (
                                <p className="subscription-refund-alert__note">
                                    {t('employee_statement_refunds_not_deducted_reason')}
                                </p>
                            )}
                            {refund.customerName && refund.dueDate && (
                                // Botão, e não link de texto: o link pequeno ficava apagado no tema e difícil de tocar no celular.
                                <Button
                                    as={Link}
                                    size="sm"
                                    variant="outline-primary"
                                    className="subscription-refund-alert__link"
                                    to={`${MenuConfig.subscription.charge.route}?${new URLSearchParams({
                                        customer: refund.customerName,
                                        dueDate: refund.dueDate,
                                        status: 'REFUNDED'
                                    }).toString()}`}
                                >
                                    {t('employee_statement_refunds_open_charge')}
                                    <i className="fa-solid fa-arrow-up-right-from-square ms-2" aria-hidden="true"></i>
                                </Button>
                            )}
                        </div>
                    ))}
                    {notDeducted.length > 0 && (
                        <div className="subscription-refund-alert__total">
                            <div>
                                <span>{t('employee_statement_refunds_not_deducted_total')}</span>
                                <strong>{formatCurrency(value)}</strong>
                            </div>
                            {hasBranchShare && (
                                <div>
                                    <span>{t('employee_statement_refunds_branch_part_total', {
                                        percent: formatPercent(value > 0 ? baseShare * 100 / value : 0)
                                    })}</span>
                                    <strong>{formatCurrency(branchDifference)}</strong>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        />
    );
};
